import React, { useState, useEffect } from "react";
import "../profile.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconCheck, IconLockOpen, IconX } from "@tabler/icons-react";
import MHeader from "@/components/ui/organisms/MHeader";
import { useProfileNavigation } from "@/config/navigation/profile-navigation";
import MLoader from "@/components/ui/atoms/MLoader";
import { getMemberInfo } from "@/hooks/useMembers";
import { MedicalDetailType, MedicalDetailStatus } from "@/types/health-profile";
import MDialog from "@/components/ui/atoms/MDialog";
import { useProfileSummary } from "@/hooks/useHealthProfileSummary";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useFamilyStats } from "@/hooks/useFamilyStats";
import { useVitals } from "@/hooks/useVitals";
import { useInsurance } from "@/hooks/useInsurance";
import { emergencyInfoService } from "@/services/emergency-info.service";
import { isChild as isChildUtil } from "@/types/member";
import familyStatsService from "@/services/familyStats.service";
import styles from "./ActivateEmergencyCard.module.css";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

interface ActivateEmergencyCardProps {
  onBack?: () => void;
  hideProfilePicker?: boolean;
}

const ActivateEmergencyCard: React.FC<ActivateEmergencyCardProps> = ({
  onBack,
  hideProfilePicker = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNav, setPendingNav] = useState<{
    path: string;
    id: string;
    type: MedicalDetailType;
  } | null>(null);
  const { healthProfileNav, userProfileNav } = useProfileNavigation();
  const memberInfo = getMemberInfo();

  // Fetch the profile summary
  const { summary, updateNone, loading } = useProfileSummary(memberInfo.id);

  // Fetch member stats to check emergencyCardReady status
  const { memberStats, fetchMemberStats } = useFamilyStats({ memberInfo });

  // Fetch vitals (pets only), insurance, and emergency contacts for completion check
  const { vitals, triggerRefetch } = useVitals(memberInfo.id);
  const { insurances, fetchInsurance } = useInsurance({ memberInfo });
  const [hasEmergencyContact, setHasEmergencyContact] = useState(false);

  // Fetch member stats on mount
  useEffect(() => {
    if (memberInfo?.id) {
      fetchMemberStats();
      fetchInsurance();
    }
  }, [memberInfo?.id, fetchMemberStats, fetchInsurance]);

  // Fetch emergency contact on mount
  useEffect(() => {
    const fetchEmergencyContact = async () => {
      if (memberInfo?.id) {
        try {
          const data = await emergencyInfoService.getEmergencyInfo(
            memberInfo.id
          );
          // Check if emergency contact exists and has required fields
          setHasEmergencyContact(
            !!(data?.family_contact?.name && data?.family_contact?.phone)
          );
          // Refetch member stats after emergency info is loaded
          // This ensures emergencyCardReady flag is updated based on backend check
          await fetchMemberStats();
        } catch (error) {
          setHasEmergencyContact(false);
        }
      }
    };
    fetchEmergencyContact();
  }, [memberInfo?.id, fetchMemberStats]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_CREATE_STEP_1,
    });
  }, []);

  // Refetch data when component becomes active (navigating back from vitals/insurance/contacts)
  useEffect(() => {
    const refetchData = async () => {
      if (memberInfo?.id) {
        triggerRefetch();
        await fetchInsurance();
        await fetchMemberStats();

        // Refetch emergency contact
        try {
          const data = await emergencyInfoService.getEmergencyInfo(
            memberInfo.id
          );
          setHasEmergencyContact(
            !!(data?.family_contact?.name && data?.family_contact?.phone)
          );
        } catch (error) {
          setHasEmergencyContact(false);
        }
      }
    };

    // Refetch on mount and when dependencies change
    refetchData();
  }, [memberInfo?.id, triggerRefetch, fetchInsurance, fetchMemberStats]);

  // Check if member is a child
  const isChild = isChildUtil({
    dobYear: memberInfo.dobYear,
    dobMonth: memberInfo.dobMonth,
    dobDay: memberInfo.dobDay,
  });

  // Check if member is a pet
  const isPet = memberInfo.memberType === "p";

  // Emergency card sections in order: essential fields first, then remaining modules
  // Pets: vitals → petCare → surgeries → deviceImplants → vaccineTracker → insurance → note → contacts
  // Children: birthComplications after medicine, vaccineTracker after deviceImplants
  const orderedSectionIds = [
    "bloodType",
    "allergies",
    "conditions",
    // 'medicine' intentionally omitted (EH-1023) — Pillbox is now the
    // canonical place to manage medications; hidden from this checklist
    // too, same as the Health Profile dashboard/sidebar.
    ...(isChild ? ["birthComplications"] : []),
    ...(isPet ? ["vitals"] : []),
    ...(isPet ? ["petCare"] : []),
    "surgeries",
    "deviceImplants",
    ...(isChild || isPet ? ["vaccineTracker"] : []),
    "insurance",
    "note",
    "contacts",
  ];

  // Create a map of all available sections
  // contacts is not in userProfileNav JSON so it is added manually
  let allSections = [
    ...healthProfileNav,
    userProfileNav.find((item) => item.id === "vitals"),
    userProfileNav.find((item) => item.id === "insurance"),
    {
      id: "contacts",
      label: "Emergency contacts",
      labelKey: "navigation.contacts",
      path: "/profile/basic-information/contact",
      derivedTypes: ["A", "C", "P"],
      icon: null,
    },
  ].filter(Boolean);

  // For pets, remove birthComplications if it exists
  if (isPet) {
    allSections = allSections.filter(
      (item) => item.id !== "birthComplications"
    );
  }

  // For adults, remove vaccineTracker
  if (!isChild && !isPet) {
    allSections = allSections.filter((item) => item.id !== "vaccineTracker");
  }

  // For children (but not pets), manually add birthComplications if not already present
  // This is needed because healthProfileNav is filtered by derivedMemberType, not age
  if (
    isChild &&
    !isPet &&
    !allSections.find((item) => item.id === "birthComplications")
  ) {
    const birthComplicationsSection = {
      id: "birthComplications",
      label: t("healthProfile.nav.birthInformation"),
      labelKey: "healthProfile.nav.birthInformation",
      path: "/profile/health-profile/birth-complications",
      derivedTypes: ["C"],
      icon: null,
    };
    allSections.push(birthComplicationsSection as any);
  }

  // For pets, manually add petCare if not already present
  if (isPet && !allSections.find((item) => item.id === "petCare")) {
    const petCareSection = {
      id: "petCare",
      label: t("healthProfile.nav.petCare"),
      labelKey: "healthProfile.nav.petCare",
      path: "/profile/health-profile/pet-care",
      derivedTypes: ["P"],
      icon: null,
    };
    allSections.push(petCareSection as any);
  }

  const sectionMap = new Map(
    allSections.map((section) => [section.id, section])
  );

  // Order sections according to the specified order
  const emergencyCardSections = orderedSectionIds
    .map((id) => sectionMap.get(id))
    .filter(Boolean);

  const getType = (id: string) => {
    const values = Object.values(MedicalDetailType);
    return values.find((value) => value === id);
  };

  // Helper function to get display value from summary
  const getDisplayFromSummary = (
    type: MedicalDetailType
  ): { message: string; isEmpty: boolean } => {
    if (!summary?.summary)
      return { message: t("common.notAnswered"), isEmpty: true };

    const detail = summary.summary[type];
    if (!detail)
      return {
        message:
          type === MedicalDetailType.NOTE
            ? t("healthProfile.placeholders.noNote")
            : t("common.notAnswered"),
        isEmpty: true,
      };

    if (detail.status === MedicalDetailStatus.NOT_ANSWERED) {
      return { message: t("common.notAnswered"), isEmpty: true };
    } else if (detail.status === MedicalDetailStatus.NONE) {
      return { message: t("healthProfile.placeholders.none"), isEmpty: false };
    } else if (detail.status === MedicalDetailStatus.ANSWERED) {
      if (detail.count && typeof detail.count === "number") {
        return {
          message: t(
            `healthProfile.${type}.count.${
              detail.count === 1 ? "singular" : "plural"
            }`,
            { count: detail.count }
          ),
          isEmpty: false,
        };
      } else if (detail.value) {
        if (type === MedicalDetailType.BLOOD_TYPE) {
          return {
            message: t(`healthProfile.bloodType.${detail.value}`),
            isEmpty: false,
          };
        }
        return {
          message: t(`healthProfile.${type}.value`, { value: detail.value }),
          isEmpty: false,
        };
      } else {
        return { message: t(`healthProfile.${type}.answered`), isEmpty: false };
      }
    }
    return { message: t("common.notAnswered"), isEmpty: true };
  };

  const handleNone = async (type: MedicalDetailType) => {
    try {
      await updateNone(type);
      setShowDialog(false);
      setPendingNav(null);
    } catch (error) {
      showToast({
        message: "Failed to update preference. Please try again.",
        variant: "error",
      });
    }
  };

  const SCROLL_KEY = `ec_scroll_${memberInfo.id}`;

  // Restore scroll position after data loads on return navigation
  const scrollRestored = React.useRef(false);
  useEffect(() => {
    if (!loading && summary && !scrollRestored.current) {
      const saved = localStorage.getItem(SCROLL_KEY);
      if (saved) {
        const y = parseInt(saved, 10);
        // Scroll the Layout's <main> container (overflow-auto), not window
        const scroller = document.querySelector("main");
        setTimeout(
          () =>
            scroller?.scrollTo({
              top: y,
              behavior: "instant" as ScrollBehavior,
            }),
          80
        );
        localStorage.removeItem(SCROLL_KEY);
      }
      scrollRestored.current = true;
    }
  }, [loading, summary]);

  const handleItemClick = (item: any) => {
    // Save scroll position before navigating away (use main scroller, not window)
    const scroller = document.querySelector("main");
    localStorage.setItem(SCROLL_KEY, String(scroller?.scrollTop ?? 0));

    // For non-health profile sections (vitals, contacts), navigate directly
    if (["vitals", "contacts"].includes(item.id)) {
      navigate(item.path, {
        state: { backUrl: window.location.pathname },
      });
      return;
    }

    // Special handling for insurance - check insurances array and summary
    if (item.id === "insurance") {
      const hasInsurance = insurances && insurances.length > 0;
      const insuranceSummary = summary?.summary?.[MedicalDetailType.INSURANCE];

      if (hasInsurance) {
        // Navigate to insurance page if policies exist
        navigate(item.path, {
          state: { backUrl: window.location.pathname },
        });
      } else if (insuranceSummary?.status === MedicalDetailStatus.NONE) {
        // User previously clicked "No" - navigate to add insurance or show option to change
        navigate(item.path, {
          state: { backUrl: window.location.pathname },
        });
      } else {
        // Show modal if no insurance and not marked as None
        setPendingNav({
          path: item.path,
          id: item.id,
          type: MedicalDetailType.INSURANCE,
        });
        setShowDialog(true);
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.INSURANCE_POPUP,
        });
      }
      return;
    }

    const type = getType(item.id);
    if (
      [
        MedicalDetailType.BLOOD_TYPE,
        MedicalDetailType.VACCINE_TRACKER,
        MedicalDetailType.PET_CARE,
      ].includes(type)
    ) {
      navigate(item.path, {
        state: { backUrl: window.location.pathname },
      });
      return;
    }

    const detail = summary.summary[type];
    // Determine if the section has data using summary
    if ([MedicalDetailStatus.ANSWERED].includes(detail?.status)) {
      navigate(item.path, {
        state: { backUrl: window.location.pathname },
      });
    } else if ([MedicalDetailStatus.NONE].includes(detail?.status)) {
      navigate(item.defaultState || item.path, {
        state: { backUrl: window.location.pathname },
      });
    } else {
      // Use defaultState if present
      const targetPath = item.defaultState || item.path;
      setPendingNav({ path: targetPath, id: item.id, type });
      setShowDialog(true);

      // Track popup for long-term conditions
      if (item.id === "conditions") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.LONG_TERM_CONDITION_POPUP,
        });
      }

      // Track popup for medicine
      if (item.id === "medicine") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.MEDICINE_POPUP,
        });
      }

      // Track popup for surgeries
      if (item.id === "surgeries") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_POPUP,
        });
      }

      // Track popup for device implants
      if (item.id === "deviceImplants") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.IMPLANT_POPUP,
        });
      }

      // Track popup for emergency note
      if (item.id === "note") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_NOTE_POPUP,
        });
      }
    }
  };

  // Calculate progress based on real data
  const completedCount = emergencyCardSections.filter((item) => {
    const type = getType(item.id);
    // For non-health profile items, check if they exist
    if (item.id === "vitals") {
      const vitalsArray =
        vitals &&
        typeof vitals === "object" &&
        "vitals" in vitals &&
        Array.isArray((vitals as any).vitals)
          ? (vitals as any).vitals
          : [];
      return vitalsArray.length > 0;
    }
    if (item.id === "contacts") {
      return hasEmergencyContact;
    }
    // Special handling for insurance
    if (item.id === "insurance") {
      const hasInsurance = insurances && insurances.length > 0;
      const insuranceSummary = summary?.summary?.[type];
      // Count as completed if has insurance OR marked as None
      return (
        hasInsurance || insuranceSummary?.status === MedicalDetailStatus.NONE
      );
    }
    const { isEmpty } = getDisplayFromSummary(type);
    return !isEmpty;
  }).length;
  const totalCount = emergencyCardSections.length;
  const progress = Math.round((completedCount / totalCount) * 100);

  // Use emergencyCardReady from member stats or member object to determine if card is ready
  // Check both sources to ensure we catch the flag from either the stats or the member object
  const isCardReady =
    memberStats?.emergencyCardReady ?? memberInfo.emergencyCardReady ?? false;

  // Check if all sections are completed (100% progress)
  const isAllSectionsComplete = progress === 100;

  // Backup: Mark emergency card complete when all sections are done
  useEffect(() => {
    const markCardComplete = async () => {
      if (
        isAllSectionsComplete &&
        memberInfo?.current_family &&
        memberInfo?.id
      ) {
        try {
          await familyStatsService.markEmergencyCardComplete(memberInfo.id);
          await fetchMemberStats();
        } catch (error) {
          console.error("Failed to mark emergency card complete:", error);
        }
      }
    };
    markCardComplete();
  }, [
    isAllSectionsComplete,
    memberInfo?.current_family,
    memberInfo?.id,
    fetchMemberStats,
  ]);

  // Show success toast when card is ready — only once per member (persisted in localStorage)
  const unlockedKey = `ec_unlocked_toast_seen_${memberInfo.id}`;
  const [showUnlockedToast, setShowUnlockedToast] = useState(
    () => !localStorage.getItem(unlockedKey)
  );

  // Persist "seen" the moment the toast is actually rendered, so it doesn't reappear
  // on subsequent visits regardless of how the user leaves (View card, back nav, etc.).
  useEffect(() => {
    if (isCardReady && showUnlockedToast) {
      localStorage.setItem(unlockedKey, "1");
    }
  }, [isCardReady, showUnlockedToast, unlockedKey]);

  // Track initial emergency card ready state to detect first unlock
  const initialCardReadyState = React.useRef<boolean | null>(null);
  const hasTrackedFirstUnlock = React.useRef(false);

  // Store initial card ready state on first render
  useEffect(() => {
    if (initialCardReadyState.current === null) {
      const initialState =
        memberStats?.emergencyCardReady ??
        memberInfo.emergencyCardReady ??
        false;
      initialCardReadyState.current = initialState;
    }
  }, [memberStats?.emergencyCardReady, memberInfo.emergencyCardReady]);

  // Track when card becomes unlocked (emergencyCardReady changes from false to true)
  useEffect(() => {
    if (
      initialCardReadyState.current !== null &&
      !hasTrackedFirstUnlock.current
    ) {
      const wasNotReady = initialCardReadyState.current === false;
      const isNowReady = isCardReady === true;

      if (wasNotReady && isNowReady) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
          screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_CREATE_STEP_1,
          action: "first_emergency_card_completed",
        });
        hasTrackedFirstUnlock.current = true;
      }
    }
  }, [isCardReady, memberInfo]);

  const handleViewCard = () => {
    navigate("/dashboard/emergency-card", {
      state: { backUrl: window.location.pathname },
    });
  };

  if (loading || !summary) {
    return (
      <div>
        <MLoader size={40} />
      </div>
    );
  }

  return (
    <div style={{ padding: "0 24px" }}>
      <MHeader
        subHeading={t("header.emergency-card.A.title")}
        module="emergency-card"
        memberInfo={memberInfo}
        showGradient
        showProfilePickerRow={!hideProfilePicker}
        onBack={onBack}
      />

      <MSpacer mt={30} />

      <p className={styles.essentialText}>
        <span className={styles.essentialAsterisk}>*</span>
        {t("emergencyCard.essentialText")}
      </p>

      <MSpacer mt={16} />

      <div>
        {/* Show unlocked toast when emergencyCardReady is true */}
        {isCardReady && showUnlockedToast ? (
          <div className={styles.successToast}>
            <div className={styles.toastIconWrapper}>
              <IconLockOpen size={20} className={styles.toastIcon} />
            </div>
            <div className={styles.toastContent}>
              <div className={styles.toastTitle}>Emergency card generated.</div>
              <div className={styles.toastDescription}>
                Add remaining details to make it complete.
              </div>
              <button className={styles.toastCta} onClick={handleViewCard}>
                View card
              </button>
            </div>
            <button
              className={styles.toastClose}
              onClick={() => {
                localStorage.setItem(unlockedKey, "1");
                setShowUnlockedToast(false);
              }}
            >
              <IconX size={20} />
            </button>
          </div>
        ) : null}

        <MSpacer mt={16} />

        {/* Sections List */}
        <div className={styles.sectionsContainer}>
          <div className="profile-list">
            {emergencyCardSections.map((item) => {
              // Get the translated label - use special label for emergency contacts
              const label =
                item.id === "contacts"
                  ? "Emergency contacts"
                  : t(item.labelKey);

              // For non-health profile sections, show "Not Answered"
              let message = "";
              let isEmpty = true;
              let isCompleted = false;

              if (item.id === "vitals") {
                const vitalsArray =
                  vitals &&
                  typeof vitals === "object" &&
                  "vitals" in vitals &&
                  Array.isArray((vitals as any).vitals)
                    ? (vitals as any).vitals
                    : [];
                if (vitalsArray.length > 0) {
                  message = "Added";
                  isEmpty = false;
                  isCompleted = true;
                } else {
                  message = t("common.notAnswered");
                  isEmpty = true;
                  isCompleted = false;
                }
              } else if (item.id === "contacts") {
                if (hasEmergencyContact) {
                  message = "Added";
                  isEmpty = false;
                  isCompleted = true;
                } else {
                  message = t("common.notAnswered");
                  isEmpty = true;
                  isCompleted = false;
                }
              } else if (item.id === "insurance") {
                // Special handling for insurance - check both insurances array and summary
                const hasInsurance = insurances && insurances.length > 0;
                const type = getType(item.id);
                const insuranceSummary = summary?.summary?.[type];

                if (hasInsurance) {
                  const count = insurances.length;
                  message = `${count} insurance polic${
                    count === 1 ? "y" : "ies"
                  }`;
                  isEmpty = false;
                  isCompleted = true;
                } else if (
                  insuranceSummary?.status === MedicalDetailStatus.NONE
                ) {
                  // User clicked "No" - mark as None
                  message = t("healthProfile.placeholders.none");
                  isEmpty = false;
                  isCompleted = true;
                } else {
                  message = t("common.notAnswered");
                  isEmpty = true;
                  isCompleted = false;
                }
              } else {
                const type = getType(item.id);
                // Custom logic for each field using summary data
                const displayData = getDisplayFromSummary(type);
                message = displayData.message;
                isEmpty = displayData.isEmpty;
                isCompleted = !isEmpty;
              }

              const isEssential = [
                "bloodType",
                "allergies",
                "conditions",
                "medicine",
              ].includes(item.id);

              return (
                <div
                  key={item.id}
                  className={styles.sectionItem}
                  onClick={() => handleItemClick(item)}
                >
                  <div className={styles.sectionCheckbox}>
                    {isCompleted ? (
                      <div className={styles.checkboxCompleted}>
                        <IconCheck size={14} strokeWidth={2.5} />
                      </div>
                    ) : (
                      <svg
                        className={styles.checkboxEmpty}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <circle
                          cx="10"
                          cy="10"
                          r="9"
                          stroke="currentColor"
                          strokeWidth="1.6"
                        />
                      </svg>
                    )}
                  </div>
                  <div className={styles.sectionContent}>
                    <div className={styles.sectionLabel}>
                      {label}
                      {isEssential && (
                        <span className={styles.requiredAsterisk}>*</span>
                      )}
                    </div>
                    <div
                      className={
                        isEmpty ? styles.sectionInfoEmpty : styles.sectionInfo
                      }
                    >
                      {message}
                    </div>
                  </div>
                  <div className={styles.sectionChevron}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {pendingNav && (
          <MDialog
            isOpen={showDialog}
            onClose={() => {
              setShowDialog(false);
              setPendingNav(null);
            }}
            title={t(
              `healthProfile.${pendingNav.type}.none.title${
                memberInfo?.onBehalf ? "_on_behalf" : ""
              }`
            )}
            description={
              pendingNav.type === MedicalDetailType.INSURANCE ? (
                <span className={styles.insuranceModalDescription}>
                  {t(`healthProfile.${pendingNav.type}.none.description`)}
                </span>
              ) : (
                t(`healthProfile.${pendingNav.type}.none.description`)
              )
            }
            primaryCtaText={t("common.yes")}
            onPrimaryCta={() => {
              setShowDialog(false);

              // Track when Yes is clicked for conditions
              if (pendingNav.id === "conditions") {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.LONG_TERM_CONDITION_ADD,
                });
              }

              // Track when Yes is clicked for medicine
              if (pendingNav.id === "medicine") {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.MEDICINE_ADD,
                });
              }

              // Track when Yes is clicked for surgeries
              if (pendingNav.id === "surgeries") {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_ADD,
                });
              }

              // Track when Yes is clicked for device implants
              if (pendingNav.id === "deviceImplants") {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.IMPLANT_ADD,
                });
              }

              // Track when Yes is clicked for emergency note
              if (pendingNav.id === "note") {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_NOTE_ADD,
                });
              }

              // Track when Yes is clicked for insurance
              if (pendingNav.id === "insurance") {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName:
                    ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_INSURANCE_ADD_POLICY,
                });
              }

              navigate(pendingNav.path, {
                state: { backUrl: window.location.pathname },
              });
              setPendingNav(null);
            }}
            secondaryCtaText={t("common.no")}
            onSecondaryCta={() => {
              handleNone(pendingNav.type);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ActivateEmergencyCard;
